import * as globalWindow from 'client/globalWindow'

import { TokenActions } from '../actions/actionTypes'
import { PrnInterface } from '../Interfaces/PrnInterface'
import { TokenInterface } from '../Interfaces/TokenInterface'
import { Immutable } from '../Other/Immutable'
import { ReducerBuilder } from './ReducerBuilder'

const INVOICING_DOMAIN = 'invoicing.shine.co'
const TEST_INVOICING_DOMAIN = 'invoicing.staging.shine.co'
export const TOKEN_NAME = 'ZervantSession'
export const processToken = (token): TokenInterface => {
  const tokenJson = JSON.parse(atob(token.split('.')[1]))

  // Prase prn from prn string
  // v1:u166795:c178288:pZERVANT:iserver:ruser,admin
  const prnString = tokenJson.prn.split(':')
  const prn: PrnInterface = {
    version: '',
    userId: 0,
    companyId: 0,
    provider: 'ZERVANT',
    roles: [],
  }

  for (const item of prnString) {
    const control = item.charAt(0)
    const value = item.substr(1)

    switch (control) {
      case 'v':
        prn.version = value
        break
      case 'u':
        prn.userId = parseInt(value)
        break
      case 'c':
        prn.companyId = parseInt(value)
        break
      case 'p':
        prn.provider = value
        break
      case 'r':
        prn.roles = value.split(',')
        break
    }
  }

  tokenJson.token = token
  tokenJson.prn = prn
  return tokenJson as TokenInterface
}

export const isTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return false
  }
  const parsedToken: TokenInterface = processToken(token)
  return (parsedToken?.exp || 0) < Math.floor(Date.now() / 1000)
}

export const isSessionTokenExpired = (): boolean => {
  return isTokenExpired(sessionStorage.getItem(TOKEN_NAME))
}

const processAuthCookies = () => {
  const cookiePrefix = window.location.hostname === TEST_INVOICING_DOMAIN ? 'ZervantTest' : 'Zervant'

  if (!document.cookie.match(`${cookiePrefix}Session`) || !document.cookie.match(`${cookiePrefix}Access`)) {
    return
  }

  globalWindow.sessionStorage.setItem(
    'ZervantSession',
    document.cookie.replace(new RegExp(`(?:^|.*;\\s*)${cookiePrefix}Session\\s*=\\s*([^;]*).*$|^.*$`), '$1'),
  )
  globalWindow.sessionStorage.setItem(
    'ZervantAccess',
    document.cookie.replace(new RegExp(`(?:^|.*;\\s*)${cookiePrefix}Access\\s*=\\s*([^;]*).*$|^.*$`), '$1'),
  )
  globalWindow.sessionStorage.setItem(
    'ZervantRefresh',
    document.cookie.replace(new RegExp(`(?:^|.*;\\s*)${cookiePrefix}Refresh\\s*=\\s*([^;]*).*$|^.*$`), '$1'),
  )

  const cookieDomain = window.location.hostname.replace('invoicing', '')

  // Remove token cookies
  document.cookie = `${cookiePrefix}Session=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${cookieDomain}; path=/`
  document.cookie = `${cookiePrefix}Access=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${cookieDomain}; path=/`
  document.cookie = `${cookiePrefix}Refresh=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${cookieDomain}; path=/`
}

type StateType = TokenInterface
type PayloadType = string
let initialState = null

processAuthCookies()

if (globalWindow && globalWindow.sessionStorage) {
  try {
    initialState = processToken(globalWindow.sessionStorage.getItem(TOKEN_NAME))
  } catch (e) {
    // Check if its an externally loaded module
    // like Online Payments recipients page
    if (!(globalWindow.initialConfiguration && globalWindow.initialConfiguration.externallyImportedModule)) {
      sessionStorage.clear()
      localStorage.clear()
      globalWindow.getLocation().assign('/login/login')
      // an error has to happend, otherwise sessionStorage will not be clean
      // containing "null" values which break redux on next login

      if (globalWindow.sessionStorage.getItem(TOKEN_NAME)) {
        throw new Error('Invalid token')
      }
    }
  }
}

function _assignLocale(state: StateType, payload: string): StateType {
  const newState = Immutable.copy(state)
  newState.locale = payload
  return newState
}

function _updateToken(state: StateType, payload: string): StateType {
  const newState = Immutable.copy(state)
  if (!payload) {
    return newState
  }
  return processToken(payload)
}

const reducer = ReducerBuilder.create<StateType, PayloadType | string>(initialState)
  .mapAction(TokenActions.CHANGE_LOCALE, _assignLocale)
  .mapAction(TokenActions.UPDATE_TOKEN, _updateToken)
  .build()

export default reducer
